import { Fragment, useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { ArrowLeft, Clock, Download, FileStack, LogOut, ShieldCheck, ShieldPlus, UploadCloud, UserPlus, Users, X } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import AdminContent from "@/pages/AdminContent";
import SEO from "@/components/SEO";

const adminSchema = z.object({
  name: z.string().trim().min(2, "Please enter a full name"),
  email: z.string().trim().email("Enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const FIELD_CLASS =
  "w-full rounded-xl border border-navy-900/15 bg-white px-4 py-2.5 text-sm text-navy-900 outline-none transition-colors placeholder:text-slate-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-400/20";

// Per-application status (application_status table). `received` is the state an
// application starts in before staff touch it.
const APP_STATUS = {
  received: { label: "Received", className: "bg-slate-100 text-slate-600 border-slate-200" },
  in_progress: { label: "In Progress", className: "bg-gold-500/10 text-gold-700 border-gold-300/50" },
  completed: { label: "Completed", className: "bg-emerald2-500/10 text-emerald2-700 border-emerald2-300/40" },
  rejected: { label: "Rejected", className: "bg-red-50 text-red-600 border-red-200" },
};

// Application-level filter for the clients table. "Completed" means every one
// of that client's documents has been reviewed; "In progress" means at least
// one is still pending.
const CLIENT_FILTERS = [
  { id: "all", label: "All clients", match: () => true },
  { id: "completed", label: "All applications completed", match: (c) => c.application_count > 0 && c.completed_count === c.application_count },
  { id: "open", label: "Has open applications", match: (c) => c.open_count > 0 },
  { id: "rejected", label: "Has rejected", match: (c) => c.rejected_count > 0 },
  { id: "none", label: "No applications yet", match: (c) => c.application_count === 0 },
  { id: "unassigned", label: "Unassigned", match: (c) => !c.subadmin_id },
];

const STATUS_STYLE = {
  pending: "bg-gold-500/10 text-gold-700 border-gold-300/50",
  reviewed: "bg-emerald2-500/10 text-emerald2-700 border-emerald2-300/40",
};

const StatCard = ({ label, value, icon: SIcon }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
      <SIcon className="h-5 w-5" />
    </span>
    <div>
      <div className="text-2xl font-bold text-navy-900">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  </div>
);

// Brand palette only (navy / gold / emerald2) — matches the rest of the dashboard.
const ReviewStatusChart = ({ pending, reviewed }) => {
  const data = [
    { name: "Pending", value: pending, color: "#d4a24c" },
    { name: "Reviewed", value: reviewed, color: "#10b981" },
  ];
  if (pending + reviewed === 0) return null;
  return (
    <div className="mt-6 h-56 w-full rounded-2xl border border-slate-200 bg-white p-4" data-testid="admin-review-chart">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
            {data.map((d) => (
              <Cell key={d.name} fill={d.color} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ borderRadius: 12, borderColor: "#e2e8f0", fontSize: 13 }} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// A client's uploads grouped by the service they applied for.
// Staff notes on an account. Saved explicitly rather than on every keystroke.
const RemarksCard = ({ token, account, label = "Remarks", onSaved }) => {
  const [value, setValue] = useState(account.remarks || "");
  const [saving, setSaving] = useState(false);
  const dirty = value.trim() !== (account.remarks || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.setRemarks(token, account.id, value);
      toast.success("Remarks saved.");
      await onSaved?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-5" data-testid={`remarks-card-${account.id}`}>
      <label htmlFor={`remarks-${account.id}`} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </label>
      <textarea
        id={`remarks-${account.id}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="Internal notes — visible to admins and the assigned subadmin only."
        data-testid={`remarks-input-${account.id}`}
        className={FIELD_CLASS}
      />
      <button
        type="button"
        onClick={save}
        disabled={saving || !dirty}
        data-testid={`remarks-save-${account.id}`}
        className="mt-3 rounded-full bg-navy-900 px-5 py-2 text-xs font-semibold text-gold-400 disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save Remarks"}
      </button>
    </div>
  );
};

const OTHER_SERVICE = "__other__";

// Staff uploading documents on a client's behalf. The service list is the CMS
// service catalogue (same source the client's own upload page uses), so slugs
// and titles always match a real service rather than being typed by hand.
const StaffUploadCard = ({ token, client, onUploaded }) => {
  const [services, setServices] = useState([]);
  const [slug, setSlug] = useState("");
  const [otherService, setOtherService] = useState("");
  const [files, setFiles] = useState([]);
  const [labels, setLabels] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api
      .listServices()
      .then(({ services: rows }) =>
        setServices(
          rows.map((r) => ({
            slug: r.slug,
            category: r.category,
            title: r.title,
            // The service's own "Documents Required" section, flattened into a
            // pick-list of document types.
            docTypes: Array.from(new Set((r.sections?.documents?.groups || []).flatMap((g) => g.items || []))),
          }))
        )
      )
      .catch(() => {});
  }, []);

  const slugify = (label) => label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  // "Other" covers work that isn't in the CMS catalogue yet — staff name it and
  // the slug is derived from that name, so it still groups as one application.
  const isOther = slug === OTHER_SERVICE;
  const service = isOther
    ? otherService.trim()
      ? { slug: slugify(otherService) || "other-service", category: "Other Services", title: otherService.trim(), docTypes: [] }
      : null
    : services.find((sv) => sv.slug === slug);

  // One row per selected file so each can be tagged with what it is.
  const addFiles = (selected) => {
    setFiles((prev) => [...prev, ...selected.filter((f) => !prev.some((p) => p.name === f.name && p.size === f.size))]);
  };

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
    setLabels((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!service || files.length === 0) return;
    const body = new FormData();
    body.append("serviceCategory", service.category);
    body.append("serviceSlug", service.slug);
    body.append("serviceTitle", service.title);
    body.append("labels", JSON.stringify(files.map((f) => labels[f.name] || "Other")));
    files.forEach((f) => body.append("files", f));

    setUploading(true);
    try {
      await api.uploadDocumentsFor(token, client.id, body);
      toast.success(`${files.length} document${files.length > 1 ? "s" : ""} uploaded for ${client.name}.`);
      setFiles([]);
      setLabels({});
      setOtherService("");
      await onUploaded?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-5" data-testid="staff-upload-form">
      <h4 className="flex items-center gap-2 text-sm font-bold text-navy-900">
        <UploadCloud className="h-4 w-4" /> Upload for this client
      </h4>
      <div className="mt-4 space-y-3">
        <div>
          <label htmlFor="staff-upload-service" className="mb-1.5 block text-xs font-semibold text-navy-800">Service</label>
          <select
            id="staff-upload-service"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            data-testid="staff-upload-service"
            className={FIELD_CLASS}
          >
            <option value="">Select a service...</option>
            {services.map((sv) => (
              <option key={sv.slug} value={sv.slug}>
                {sv.category} — {sv.title}
              </option>
            ))}
            <option value={OTHER_SERVICE}>Other (not listed)</option>
          </select>
        </div>

        {isOther && (
          <div>
            <label htmlFor="staff-upload-other" className="mb-1.5 block text-xs font-semibold text-navy-800">
              Service name
            </label>
            <input
              id="staff-upload-other"
              type="text"
              value={otherService}
              onChange={(e) => setOtherService(e.target.value)}
              placeholder="e.g. Trademark Objection Reply"
              data-testid="staff-upload-other"
              className={FIELD_CLASS}
            />
          </div>
        )}

        <div>
          <label htmlFor="staff-upload-files" className="mb-1.5 block text-xs font-semibold text-navy-800">
            Documents (PDF, JPG, PNG — select several at once)
          </label>
          <input
            id="staff-upload-files"
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => {
              addFiles(Array.from(e.target.files || []));
              e.target.value = "";
            }}
            data-testid="staff-upload-files"
            className="w-full text-xs text-slate-600"
          />
        </div>

        {files.length > 0 && (
          <ul className="space-y-2" data-testid="staff-upload-file-list">
            {files.map((f) => (
              <li key={f.name} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-xs text-navy-800">{f.name}</span>
                {service && service.docTypes.length === 0 && (
                  <input
                    type="text"
                    value={labels[f.name] || ""}
                    onChange={(e) => setLabels((prev) => ({ ...prev, [f.name]: e.target.value }))}
                    placeholder="Document type"
                    data-testid={`staff-upload-label-${f.name}`}
                    className="w-40 rounded-lg border border-navy-900/15 bg-white px-2 py-1 text-xs text-navy-900"
                  />
                )}
                {service && service.docTypes.length > 0 && (
                  <select
                    value={labels[f.name] || ""}
                    onChange={(e) => setLabels((prev) => ({ ...prev, [f.name]: e.target.value }))}
                    data-testid={`staff-upload-label-${f.name}`}
                    className="rounded-lg border border-navy-900/15 bg-white px-2 py-1 text-xs text-navy-900"
                  >
                    <option value="">Document type...</option>
                    {service.docTypes.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(f.name)}
                  aria-label={`Remove ${f.name}`}
                  className="text-slate-400 transition-colors hover:text-red-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <button
          type="submit"
          disabled={uploading || !service || files.length === 0}
          data-testid="staff-upload-submit"
          className="rounded-full bg-navy-900 px-5 py-2 text-xs font-semibold text-gold-400 disabled:opacity-50"
        >
          {uploading ? "Uploading..." : `Upload${files.length ? ` ${files.length} document${files.length > 1 ? "s" : ""}` : ""}`}
        </button>
      </div>
    </form>
  );
};

const ClientApplications = ({ client, documents, applicationStatuses, token, onBack, onToggleStatus, onDownload, onRefresh, onSetAppStatus }) => {
  const applications = useMemo(() => {
    const statusOf = new Map((applicationStatuses || []).map((a) => [a.service_slug, a.status]));
    const bySlug = new Map();
    documents.forEach((d) => {
      if (!bySlug.has(d.service_slug)) {
        bySlug.set(d.service_slug, {
          slug: d.service_slug,
          category: d.service_category,
          title: d.service_title,
          status: statusOf.get(d.service_slug) || "received",
          docs: [],
        });
      }
      bySlug.get(d.service_slug).docs.push(d);
    });
    return Array.from(bySlug.values());
  }, [documents, applicationStatuses]);

  return (
    <div className="mt-6" data-testid="admin-client-detail">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-700 transition-colors hover:text-gold-600">
        <ArrowLeft className="h-4 w-4" /> Back to Clients
      </button>
      <h3 className="mt-4 text-lg font-bold text-navy-900">{client.name}</h3>
      <p className="text-sm text-slate-500">{client.email} · {applications.length} application{applications.length !== 1 ? "s" : ""}</p>

      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <StaffUploadCard token={token} client={client} onUploaded={onRefresh} />
        <RemarksCard token={token} account={client} label="Client remarks" onSaved={onRefresh} />
      </div>

      {applications.length === 0 ? (
        <p className="mt-6 rounded-3xl border border-dashed border-navy-900/15 bg-[rgb(238,242,250)]/50 py-16 text-center text-slate-600">
          No applications yet.
        </p>
      ) : (
        <div className="mt-6 space-y-6">
          {applications.map((app) => (
            <div key={app.slug} className="overflow-hidden rounded-2xl border border-slate-200" data-testid={`admin-client-application-${app.slug}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 bg-[rgb(238,242,250)] px-5 py-3.5">
                <div>
                  <h4 className="text-sm font-bold text-navy-900">{app.title}</h4>
                  <p className="text-xs text-slate-500">{app.category} · {app.docs.length} document{app.docs.length !== 1 ? "s" : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${APP_STATUS[app.status].className}`}>
                    {APP_STATUS[app.status].label}
                  </span>
                  <select
                    value={app.status}
                    onChange={(e) => onSetAppStatus(app.slug, e.target.value)}
                    data-testid={`app-status-${app.slug}`}
                    className="rounded-lg border border-navy-900/15 bg-white px-2.5 py-1.5 text-xs font-semibold text-navy-900"
                  >
                    {Object.entries(APP_STATUS).map(([value, meta]) => (
                      <option key={value} value={value}>
                        {meta.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <table className="w-full text-left text-sm">
                <thead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-5 py-2.5">Document Type</th>
                    <th className="px-5 py-2.5">File</th>
                    <th className="px-5 py-2.5">Uploaded</th>
                    <th className="px-5 py-2.5">Status</th>
                    <th className="px-5 py-2.5">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {app.docs.map((d) => (
                    <tr key={d.id} data-testid={`admin-doc-row-${d.id}`}>
                      <td className="px-5 py-3 text-navy-700">{d.document_label}</td>
                      <td className="px-5 py-3 text-slate-600">{d.file_name}</td>
                      <td className="whitespace-nowrap px-5 py-3 text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" /> {new Date(d.uploaded_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-5 py-3">
                        <button
                          type="button"
                          onClick={() => onToggleStatus(d)}
                          data-testid={`admin-toggle-status-${d.id}`}
                          className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize transition-opacity hover:opacity-75 ${STATUS_STYLE[d.status]}`}
                        >
                          {d.status}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-5 py-3">
                        <button
                          type="button"
                          onClick={() => onDownload(d)}
                          data-testid={`admin-download-${d.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald2-600 hover:underline"
                        >
                          <Download className="h-3.5 w-3.5" /> Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Name / email / password form shared by the create-account cards. `idPrefix`
// keeps label-for ids and test ids unique when more than one is on screen.
const AccountFields = ({ register, errors, idPrefix }) => (
  <>
    {[
      { key: "name", label: "Full Name", type: "text" },
      { key: "email", label: "Email Address", type: "email" },
      { key: "password", label: "Password", type: "password" },
    ].map((f) => (
      <div key={f.key}>
        <label htmlFor={`${idPrefix}-${f.key}`} className="mb-1.5 block text-xs font-semibold text-navy-800">
          {f.label}
        </label>
        <input id={`${idPrefix}-${f.key}`} type={f.type} className={FIELD_CLASS} data-testid={`${idPrefix}-${f.key}`} {...register(f.key)} />
        {errors[f.key] && <p className="mt-1 text-xs font-medium text-red-500">{errors[f.key].message}</p>}
      </div>
    ))}
  </>
);

// Admin-only: create a client account by hand, for clients onboarded off-site.
const AddClientCard = ({ token, onCreated }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(adminSchema), defaultValues: { name: "", email: "", password: "" } });

  const onCreate = async (values) => {
    try {
      await api.clientCreate(token, values);
      toast.success(`Client account created for ${values.name}. Share the password with them to log in.`);
      reset();
      await onCreated();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onCreate)}
      className="mb-6 rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-6"
      data-testid="create-client-form"
      noValidate
    >
      <h3 className="flex items-center gap-2 text-base font-bold text-navy-900">
        <UserPlus className="h-4 w-4" /> Add Client
      </h3>
      <p className="mt-1.5 text-sm text-slate-600">
        Creates a client login. They can change the password after signing in.
      </p>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <AccountFields register={register} errors={errors} idPrefix="create-client" />
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        data-testid="create-client-submit"
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 px-6 py-2.5 text-sm font-semibold text-gold-400 transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60"
      >
        {isSubmitting ? "Creating..." : "Create Client"}
      </button>
    </form>
  );
};

// Admin-only: create subadmin accounts and assign clients to them.
// A client can be assigned to only one subadmin at a time (server enforces this).
const SubadminsPanel = ({ token, clients }) => {
  const [subadmins, setSubadmins] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [assignedByExpanded, setAssignedByExpanded] = useState([]);
  const [assignClientId, setAssignClientId] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(adminSchema), defaultValues: { name: "", email: "", password: "" } });

  const loadSubadmins = () => api.subadminList(token).then((res) => setSubadmins(res.subadmins));

  useEffect(() => {
    loadSubadmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCreate = async (values) => {
    try {
      await api.subadminCreate(token, values);
      toast.success(`Subadmin account created for ${values.name}.`);
      reset();
      await loadSubadmins();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleExpand = async (subadmin) => {
    if (expandedId === subadmin.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(subadmin.id);
    setAssignClientId("");
    const { clients: assigned } = await api.subadminClients(token, subadmin.id);
    setAssignedByExpanded(assigned);
  };

  const assign = async () => {
    if (!assignClientId) return;
    try {
      await api.assignClientToSubadmin(token, expandedId, assignClientId);
      const { clients: assigned } = await api.subadminClients(token, expandedId);
      setAssignedByExpanded(assigned);
      setAssignClientId("");
      await loadSubadmins();
      toast.success("Client assigned.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (subadmin) => {
    if (!window.confirm(`Delete the subadmin account for "${subadmin.name}"? Their clients stay, but become unassigned.`)) return;
    try {
      await api.subadminDelete(token, subadmin.id);
      toast.success("Subadmin deleted.");
      if (expandedId === subadmin.id) setExpandedId(null);
      await loadSubadmins();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const unassign = async (clientId) => {
    try {
      await api.unassignClientFromSubadmin(token, expandedId, clientId);
      setAssignedByExpanded((prev) => prev.filter((c) => c.id !== clientId));
      await loadSubadmins();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const assignedIds = new Set(assignedByExpanded.map((c) => c.id));
  const unassignedClients = clients.filter((c) => !assignedIds.has(c.id));

  return (
    <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_1fr]" data-testid="admin-subadmins-panel">
      <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid="admin-subadmins-table">
        <table className="w-full text-left text-sm">
          <thead className="bg-[rgb(238,242,250)] text-xs font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Clients</th>
              <th className="px-5 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {subadmins.map((s) => (
              <>
                <tr key={s.id} data-testid={`admin-subadmin-row-${s.id}`}>
                  <td className="px-5 py-3.5 font-medium text-navy-800">{s.name}</td>
                  <td className="px-5 py-3.5 text-slate-600">{s.email}</td>
                  <td className="px-5 py-3.5 text-navy-700">{s.client_count}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-4">
                      <button type="button" onClick={() => toggleExpand(s)} className="text-xs font-semibold text-emerald2-600 hover:underline">
                        {expandedId === s.id ? "Close" : "Manage Clients"}
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(s)}
                        data-testid={`delete-subadmin-${s.id}`}
                        className="text-xs font-semibold text-red-500 hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === s.id && (
                  <tr>
                    <td colSpan={4} className="bg-[rgb(238,242,250)]/50 px-5 py-4">
                      <div className="mb-4">
                        <RemarksCard token={token} account={s} label="Subadmin remarks" onSaved={loadSubadmins} />
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={assignClientId}
                          onChange={(e) => setAssignClientId(e.target.value)}
                          className="rounded-lg border border-navy-900/15 bg-white px-3 py-2 text-sm text-navy-900"
                        >
                          <option value="">Assign a client...</option>
                          {unassignedClients.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name} ({c.email})
                            </option>
                          ))}
                        </select>
                        <button type="button" onClick={assign} className="rounded-full bg-navy-900 px-4 py-2 text-xs font-semibold text-gold-400">
                          Assign
                        </button>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {assignedByExpanded.map((c) => (
                          <span key={c.id} className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-navy-700 shadow-sm">
                            {c.name}
                            <button type="button" onClick={() => unassign(c.id)} className="text-red-500 hover:text-red-700">
                              ×
                            </button>
                          </span>
                        ))}
                        {assignedByExpanded.length === 0 && <span className="text-xs text-slate-500">No clients assigned yet.</span>}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
            {subadmins.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-slate-500">
                  No subadmins yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-6" data-testid="admin-create-subadmin-card">
        <h3 className="flex items-center gap-2 text-base font-bold text-navy-900">
          <ShieldPlus className="h-4 w-4" /> Create Subadmin Account
        </h3>
        <p className="mt-1.5 text-sm text-slate-600">Subadmins only see clients you assign to them.</p>
        <form onSubmit={handleSubmit(onCreate)} className="mt-5 space-y-4" noValidate>
          <AccountFields register={register} errors={errors} idPrefix="create-subadmin" />
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy-900 px-6 py-2.5 text-sm font-semibold text-gold-400 transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60"
          >
            {isSubmitting ? "Creating..." : "Create Subadmin"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const { user, token, logout, isAdmin } = useAuth();
  const [tab, setTab] = useState("clients");
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [remarksAdminId, setRemarksAdminId] = useState(null);
  const [clientApps, setClientApps] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDocs, setClientDocs] = useState([]);
  const [clientDocsLoading, setClientDocsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(adminSchema), defaultValues: { name: "", email: "", password: "" } });

  const loadAll = async () => {
    const clientsRes = await api.adminUsers(token);
    setClients(clientsRes.users);
    if (isAdmin) {
      const [statsRes, adminsRes] = await Promise.all([api.adminStats(token), api.adminList(token)]);
      setStats(statsRes);
      setAdmins(adminsRes.admins);
    }
  };

  useEffect(() => {
    loadAll().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCreateAdmin = async (values) => {
    try {
      const { admin } = await api.adminCreate(token, values);
      setAdmins((prev) => [...prev, admin]);
      toast.success(`Admin account created for ${admin.name}.`);
      reset();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onDeleteAdmin = async (admin) => {
    if (!window.confirm(`Delete the admin account for "${admin.name}"? They lose dashboard access immediately.`)) return;
    try {
      await api.adminDelete(token, admin.id);
      setAdmins((prev) => prev.filter((a) => a.id !== admin.id));
      toast.success("Admin deleted.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredClients = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (CLIENT_FILTERS.find((f) => f.id === clientFilter) || CLIENT_FILTERS[0]).match;
    return clients.filter((c) => match(c) && (!q || `${c.name} ${c.email}`.toLowerCase().includes(q)));
  }, [clients, query, clientFilter]);

  // Re-pulls the client's documents and the client list, so a staff upload or a
  // saved remark is reflected without leaving the detail view.
  const refreshSelectedClient = async () => {
    if (!selectedClient) return;
    try {
      const [{ documents, applications }, { users }] = await Promise.all([
        api.adminUserDocuments(token, selectedClient.id),
        api.adminUsers(token),
      ]);
      setClientDocs(documents);
      setClientApps(applications || []);
      setClients(users);
      const fresh = users.find((u) => u.id === selectedClient.id);
      if (fresh) setSelectedClient(fresh);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const setAppStatus = async (slug, status) => {
    try {
      await api.setApplicationStatus(token, selectedClient.id, slug, status);
      toast.success("Application status updated.");
      await refreshSelectedClient();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Auth is a header, so the export can't be a plain link — fetch it and hand
  // the browser a blob.
  const exportClients = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/admin/users/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Could not export clients.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `biz360-clients-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  const openClient = async (client) => {
    setSelectedClient(client);
    setClientDocsLoading(true);
    try {
      const { documents, applications } = await api.adminUserDocuments(token, client.id);
      setClientDocs(documents);
      setClientApps(applications || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClientDocsLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await api.downloadDocument(token, doc.id, doc.file_name);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleStatus = async (doc) => {
    const nextStatus = doc.status === "pending" ? "reviewed" : "pending";
    try {
      await api.updateDocumentStatus(token, doc.id, nextStatus);
      setClientDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, status: nextStatus } : d)));
      setStats((prev) =>
        prev && {
          ...prev,
          pending: prev.pending + (nextStatus === "pending" ? 1 : -1),
          reviewed: prev.reviewed + (nextStatus === "reviewed" ? 1 : -1),
        }
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="admin-page">
      <SEO
        title="Admin Dashboard | Biz360 Compliance"
        canonical="/admin"
        robots="noindex, nofollow"
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className={`relative overflow-hidden bg-[rgb(238,242,250)] pt-32 md:pt-40 ${tab === "clients" && stats ? "pb-10 md:pb-12" : "pb-6 md:pb-8"}`}>
          <AmbientOrbs />
          <div className="relative mx-auto max-w-6xl px-5 lg:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <Reveal>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-navy-900/15 bg-white px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-navy-700">
                    <ShieldCheck className="h-3.5 w-3.5" /> {isAdmin ? "Admin" : "Subadmin"}
                  </span>
                </Reveal>
                <Reveal delay={0.05}>
                  <h1 className="mt-3 text-3xl font-bold tracking-tight text-navy-900 md:text-4xl">{isAdmin ? "Admin" : "Subadmin"} Dashboard</h1>
                </Reveal>
                <Reveal delay={0.1}>
                  <p className="mt-2 text-slate-600">Logged in as {user?.name} · {user?.email}</p>
                </Reveal>
              </div>
              <button
                type="button"
                onClick={logout}
                data-testid="admin-logout"
                className="inline-flex items-center gap-2 rounded-full border border-navy-900/15 px-5 py-2.5 text-sm font-semibold text-navy-900 transition-colors hover:border-red-300 hover:text-red-600"
              >
                <LogOut className="h-4 w-4" /> Log Out
              </button>
            </div>

            {tab === "clients" && stats && (
              <>
                <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
                  <StatCard label="Clients" value={stats.total_users} icon={Users} />
                  <StatCard label="Documents" value={stats.total_documents} icon={FileStack} />
                  <StatCard label="Pending" value={stats.pending} icon={Clock} />
                  <StatCard label="Reviewed" value={stats.reviewed} icon={ShieldCheck} />
                </div>
                <ReviewStatusChart pending={stats.pending} reviewed={stats.reviewed} />
              </>
            )}
          </div>
        </section>

        <section className="relative bg-white py-10 md:py-14">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="flex gap-2 border-b border-slate-200" data-testid="admin-tabs">
              {[
                { id: "clients", label: "Clients" },
                ...(isAdmin ? [{ id: "content", label: "Content" }, { id: "admins", label: "Admins" }, { id: "subadmins", label: "Subadmins" }] : []),
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setTab(t.id);
                    setSelectedClient(null);
                  }}
                  data-testid={`admin-tab-${t.id}`}
                  className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                    tab === t.id ? "border-gold-500 text-navy-900" : "border-transparent text-slate-500 hover:text-navy-700"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "content" ? (
              <div className="mt-6">
                <AdminContent />
              </div>
            ) : loading ? (
              <p className="py-16 text-center text-slate-500">Loading...</p>
            ) : tab === "subadmins" ? (
              <SubadminsPanel token={token} clients={clients} />
            ) : tab === "admins" ? (
              <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr]">
                <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid="admin-admins-table">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-[rgb(238,242,250)] text-xs font-bold uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-5 py-3">Name</th>
                        <th className="px-5 py-3">Email</th>
                        <th className="px-5 py-3">Joined</th>
                        <th className="px-5 py-3">Action</th>
                        <th className="px-5 py-3">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {admins.map((a) => (
                        <Fragment key={a.id}>
                        <tr data-testid={`admin-admin-row-${a.id}`}>
                          <td className="px-5 py-3.5 font-medium text-navy-800">
                            {a.name} {a.id === user?.id && <span className="ml-1 text-xs font-normal text-slate-400">(you)</span>}
                          </td>
                          <td className="px-5 py-3.5 text-slate-600">{a.email}</td>
                          <td className="px-5 py-3.5 text-slate-500">{new Date(a.created_at).toLocaleDateString()}</td>
                          <td className="px-5 py-3.5">
                            {a.id === user?.id || admins.length <= 1 ? (
                              <span className="text-xs text-slate-300">—</span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => onDeleteAdmin(a)}
                                data-testid={`delete-admin-${a.id}`}
                                className="text-xs font-semibold text-red-500 hover:underline"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            <button
                              type="button"
                              onClick={() => setRemarksAdminId((prev) => (prev === a.id ? null : a.id))}
                              data-testid={`admin-remarks-toggle-${a.id}`}
                              className="text-xs font-semibold text-emerald2-600 hover:underline"
                            >
                              {remarksAdminId === a.id ? "Close" : a.remarks ? "Edit note" : "Add note"}
                            </button>
                          </td>
                        </tr>
                        {remarksAdminId === a.id && (
                          <tr>
                            <td colSpan={5} className="bg-[rgb(238,242,250)]/50 px-5 py-4">
                              <RemarksCard
                                token={token}
                                account={a}
                                label="Admin remarks"
                                onSaved={() => api.adminList(token).then((res) => setAdmins(res.admins))}
                              />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-6" data-testid="admin-create-admin-card">
                  <h3 className="flex items-center gap-2 text-base font-bold text-navy-900">
                    <ShieldPlus className="h-4 w-4" /> Create Admin Account
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-600">New admins get full access to this dashboard.</p>
                  <form onSubmit={handleSubmit(onCreateAdmin)} className="mt-5 space-y-4" data-testid="create-admin-form" noValidate>
                    <AccountFields register={register} errors={errors} idPrefix="create-admin" />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="create-admin-submit"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy-900 px-6 py-2.5 text-sm font-semibold text-gold-400 transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60"
                    >
                      {isSubmitting ? "Creating..." : "Create Admin"}
                    </button>
                  </form>
                </div>
              </div>
            ) : selectedClient ? (
              clientDocsLoading ? (
                <p className="mt-6 py-16 text-center text-slate-500">Loading applications...</p>
              ) : (
                <ClientApplications
                  client={selectedClient}
                  documents={clientDocs}
                  applicationStatuses={clientApps}
                  token={token}
                  onBack={() => setSelectedClient(null)}
                  onToggleStatus={toggleStatus}
                  onDownload={handleDownload}
                  onRefresh={refreshSelectedClient}
                  onSetAppStatus={setAppStatus}
                />
              )
            ) : (
              <div className="mt-6">
                {isAdmin && <AddClientCard token={token} onCreated={loadAll} />}
                <div className="flex flex-wrap items-center gap-3">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  type="text"
                  placeholder="Search clients by name or email..."
                  data-testid="admin-client-search"
                  className="w-full max-w-md rounded-xl border border-navy-900/15 bg-white px-4 py-2.5 text-sm text-navy-900 outline-none transition-colors placeholder:text-slate-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-400/20"
                />
                <button
                  type="button"
                  onClick={exportClients}
                  disabled={exporting}
                  data-testid="admin-export-clients"
                  className="inline-flex shrink-0 items-center gap-2 rounded-full border border-navy-900/15 px-5 py-2.5 text-sm font-semibold text-navy-900 transition-colors hover:bg-[rgb(238,242,250)] disabled:opacity-60"
                >
                  <Download className="h-4 w-4" /> {exporting ? "Exporting..." : "Export to Excel"}
                </button>
                </div>
                <div className="mb-6 mt-3 flex flex-wrap gap-2" data-testid="admin-client-filters">
                  {CLIENT_FILTERS.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setClientFilter(f.id)}
                      data-testid={`admin-client-filter-${f.id}`}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                        clientFilter === f.id ? "bg-navy-900 text-white" : "border border-navy-900/15 text-navy-800 hover:bg-[rgb(238,242,250)]"
                      }`}
                    >
                      {f.label}
                      <span className="ml-1.5 text-[11px] font-normal opacity-70">{clients.filter(f.match).length}</span>
                    </button>
                  ))}
                </div>
                <div className="overflow-x-auto rounded-2xl border border-slate-200" data-testid="admin-clients-table">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-[rgb(238,242,250)] text-xs font-bold uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="whitespace-nowrap px-5 py-3">Name</th>
                        <th className="whitespace-nowrap px-5 py-3">Email</th>
                        <th className="whitespace-nowrap px-5 py-3">Assigned To</th>
                        <th className="whitespace-nowrap px-5 py-3">Joined</th>
                        <th className="whitespace-nowrap px-5 py-3">Applications</th>
                        <th className="whitespace-nowrap px-5 py-3">Last Upload</th>
                        <th className="whitespace-nowrap px-5 py-3">Remarks</th>
                        <th className="whitespace-nowrap px-5 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredClients.map((c) => (
                        <tr key={c.id} data-testid={`admin-client-row-${c.id}`}>
                          <td className="whitespace-nowrap px-5 py-3.5 font-medium text-navy-800">{c.name}</td>
                          <td className="whitespace-nowrap px-5 py-3.5">
                            {c.subadmin_name ? (
                              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-2.5 py-1 text-xs font-semibold text-emerald2-700">
                                Yes — {c.subadmin_name}
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-500">
                                Unassigned
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-navy-700">
                            {c.application_count === 0 ? (
                              <span className="text-slate-400">—</span>
                            ) : (
                              <span className="inline-flex items-center gap-2">
                                {c.application_count}
                                <span
                                  className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                    c.completed_count === c.application_count
                                      ? APP_STATUS.completed.className
                                      : c.rejected_count > 0
                                        ? APP_STATUS.rejected.className
                                        : APP_STATUS.in_progress.className
                                  }`}
                                >
                                  {c.completed_count === c.application_count
                                    ? "All completed"
                                    : `${c.completed_count}/${c.application_count} completed`}
                                </span>
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-slate-500">
                            {c.last_upload_at ? new Date(c.last_upload_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="max-w-[16rem] px-5 py-3.5 text-slate-500">
                            <span className="line-clamp-2 text-xs">{c.remarks || "—"}</span>
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5">
                            <button
                              type="button"
                              onClick={() => openClient(c)}
                              data-testid={`admin-view-client-${c.id}`}
                              className="text-xs font-semibold text-emerald2-600 hover:underline"
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      ))}
                      {filteredClients.length === 0 && (
                        <tr>
                          <td colSpan={8} className="px-5 py-10 text-center text-slate-500">
                            No clients match your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
